// File: frontend/src/components/ImportDrawer.tsx
import React, { useState, useRef, useMemo } from 'react';
import {
  Box,
  Button,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  LinearProgress,
  Alert,
  Chip,
  TextField,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  OutlinedInput,
  ToggleButton,
  ToggleButtonGroup,
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { apiFetch } from '../utils/api.ts';

interface ColumnMapping {
  type: 'static' | 'from_file' | 'null';
  sourceField?: string;
  staticValue?: string;
}

interface ColumnMappingConfig {
  [key: string]: ColumnMapping;
}

interface ImportDrawerProps {
  onImportSuccess?: (
    trackId: number,
    trackName: string,
    importedStartTime?: string | null,
    importedEndTime?: string | null
  ) => void;
}

// The record keys that hold data on the pre-mapped records from the backend
const RECORD_FIELDS = [
  'timestamp', 'latitude', 'longitude', 'altitude', 'speed', 'bearing',
  'accuracy', 'provider', 'comment', 'source', 'image', 'travelMode',
] as const;

const COLUMNS = [
  { key: 'timestamp', label: 'Timestamp (UTC)', nullable: false },
  { key: 'latitude', label: 'Latitude', nullable: false },
  { key: 'longitude', label: 'Longitude', nullable: false },
  { key: 'altitude', label: 'Altitude', nullable: true },
  { key: 'speed', label: 'Speed', nullable: true },
  { key: 'bearing', label: 'Bearing', nullable: true },
  { key: 'accuracy', label: 'Accuracy', nullable: true },
  { key: 'provider', label: 'Provider', nullable: true },
  { key: 'comment', label: 'Comment', nullable: true },
  { key: 'source', label: 'Source', nullable: true },
  { key: 'image', label: 'Image', nullable: true },
  { key: 'travelMode', label: 'Travel Mode', nullable: true },
];

const ImportDrawer: React.FC<ImportDrawerProps> = ({
  onImportSuccess,
}) => {
  const [parseData, setParseData] = useState<any>(null);
  const [columnMapping, setColumnMapping] = useState<ColumnMappingConfig>({});
  const [trackName, setTrackName] = useState('');
  const [unmappedFieldsToStore, setUnmappedFieldsToStore] = useState<string[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [importing, setImporting] = useState(false);
  const [importResult, setImportResult] = useState<any>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const dragOverRef = useRef(false);

  // Build the list of fields available for "From File" mapping.
  // These are the actual keys on the pre-mapped records, plus unmapped field paths.
  const availableFields = useMemo(() => {
    if (!parseData) return [];
    const fields: string[] = [];
    const allRecords = Array.isArray(parseData.allRecords) ? parseData.allRecords : [];

    // Add all standard record fields that have data in at least one parsed record
    for (const key of RECORD_FIELDS) {
      const hasData = allRecords.some((r: any) => r[key] != null);
      if (hasData) {
        fields.push(key);
      }
    }
    // Add unmapped fields (accessible via unmappedFields.fieldName)
    if (Array.isArray(parseData.availableUnmappedFields)) {
      for (const field of parseData.availableUnmappedFields) {
        fields.push(`unmappedFields.${field}`);
      }
    }
    return fields;
  }, [parseData]);

  // Raw field names from the source file (for display in the dropdown)
  const rawFieldNames = useMemo(() => {
    if (!parseData?.availableFields) return [];
    return parseData.availableFields as string[];
  }, [parseData]);

  // Build a display label for each available field
  // Maps record key -> raw source file field name
  const fieldDisplayName = useMemo(() => {
    const map: Record<string, string> = {};
    if (!parseData) return map;
    // Build reverse mapping from autoDetectedMapping: column key -> raw name
    const detected = parseData.autoDetectedMapping || {};
    const columnToRaw: Record<string, string> = {};
    for (const [colKey, info] of Object.entries(detected)) {
      const raw = (info as any)?.sourceField ?? info;
      if (typeof raw === 'string') {
        columnToRaw[colKey] = raw;
      }
    }
    // For each available field, find matching raw name
    for (const field of availableFields) {
      if (field.startsWith('unmappedFields.')) {
        const unmappedKey = field.slice('unmappedFields.'.length);
        // Find matching raw field name
        const rawMatch = rawFieldNames.find(r => r.endsWith(`.${unmappedKey}`) || r === unmappedKey);
        map[field] = rawMatch || unmappedKey;
      } else {
        // Standard record field - use the raw name from auto-detected mapping
        const rawName = columnToRaw[field];
        const rawMatch = rawFieldNames.find(r => r.endsWith(`.${rawName}`) || r === rawName);
        map[field] = rawMatch || rawName || field;
      }
    }
    return map;
  }, [parseData, availableFields, rawFieldNames]);

  // Resolve a single field value from a record using the current mapping
  const resolveValue = (record: any, key: string, mapping: ColumnMapping | undefined): any => {
    if (!mapping) return record[key];
    if (mapping.type === 'null') return null;
    if (mapping.type === 'static') return mapping.staticValue ?? null;
    if (mapping.type === 'from_file' && mapping.sourceField) {
      // Navigate dot-separated paths (e.g. "unmappedFields.someKey")
      return mapping.sourceField.split('.').reduce(
        (obj: any, k: string) => obj?.[k],
        record
      ) ?? null;
    }
    return null;
  };

  // Preview records with current mapping applied
  const previewRecords = useMemo(() => {
    if (!parseData?.preview) return [];
    return parseData.preview.slice(0, 5).map((record: any) => {
      const mapped: any = { index: record.index };
      for (const col of COLUMNS) {
        mapped[col.key] = resolveValue(record, col.key, columnMapping[col.key]);
      }
      return mapped;
    });
  }, [parseData, columnMapping]);

  const handleFileSelect = async (selectedFile: File) => {
    setError(null);

    try {
      setLoading(true);
      const formData = new FormData();
      formData.append('file', selectedFile);

      const response = await apiFetch('/api/import/preview', {
        method: 'POST',
        body: formData
      });

      if (!response.ok) {
        let msg = 'Failed to preview file';
        try {
          const errData = await response.json();
          msg = errData?.error || msg;
        } catch (e) {
          try {
            const text = await response.text();
            if (text) msg = text;
          } catch (e2) {
            /* ignore */
          }
        }
        throw new Error(msg);
      }

      let data;
      try {
        data = await response.json();
      } catch (e) {
        const text = await response.text();
        throw new Error(text || 'Failed to parse preview response');
      }

      // Build initial mapping from auto-detected mapping.
      // The backend's autoDetectedMapping has source field names (e.g. "ele" for altitude),
      // but records are already pre-mapped, so we use the record key directly.
      const initialMapping: ColumnMappingConfig = {};
      for (const column of COLUMNS) {
        const detected = data.autoDetectedMapping?.[column.key];
        const sourceField = detected?.sourceField ?? detected;

        if (sourceField != null) {
          // Auto-detected: the data is already in record[column.key]
          initialMapping[column.key] = {
            type: 'from_file',
            sourceField: column.key,
          };
        } else if (column.nullable) {
          initialMapping[column.key] = { type: 'null' };
        } else {
          initialMapping[column.key] = { type: 'static', staticValue: '' };
        }
      }

      setParseData(data);
      setColumnMapping(initialMapping);
      setTrackName(data.suggestedTrackName);
      setUnmappedFieldsToStore([]);
      setImportResult(null);
    } catch (err: any) {
      setError(err.message || 'Failed to parse file');
    } finally {
      setLoading(false);
    }
  };

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    dragOverRef.current = true;
  };

  const handleDragLeave = () => {
    dragOverRef.current = false;
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    dragOverRef.current = false;
    if (e.dataTransfer.files.length > 0) {
      handleFileSelect(e.dataTransfer.files[0]);
    }
  };

  const updateMapping = (key: string, mapping: ColumnMapping) => {
    setColumnMapping(prev => ({ ...prev, [key]: mapping }));
  };

  const handleImport = async () => {
    try {
      setError(null);
      setImporting(true);

      const response = await apiFetch('/api/import/execute', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          fileData: parseData.allRecords,
          trackName,
          columnMapping,
          unmappedFieldsToStore,
          additionalDataLabels: Object.fromEntries(
            unmappedFieldsToStore.map(field => [field, fieldDisplayName[field] || field])
          )
        })
      });

      if (!response.ok) {
        let msg = 'Import failed';
        try {
          const errData = await response.json();
          msg = errData?.error || msg;
        } catch (e) {
          try {
            const text = await response.text();
            if (text) msg = text;
          } catch (e2) {
            /* ignore */
          }
        }
        throw new Error(msg);
      }

      let result;
      try {
        result = await response.json();
      } catch (e) {
        const text = await response.text();
        throw new Error(text || 'Failed to parse import response');
      }
      setImportResult(result);
      onImportSuccess?.(
        result.trackId,
        result.trackName,
        result.importedStartTime,
        result.importedEndTime
      );
    } catch (err: any) {
      setError(err.message || 'Import failed');
    } finally {
      setImporting(false);
    }
  };

  const handleReset = () => {
    setParseData(null);
    setColumnMapping({});
    setTrackName('');
    setUnmappedFieldsToStore([]);
    setError(null);
    setImportResult(null);
  };

  const formatCellValue = (value: any, key: string): string => {
    if (value == null) return '-';
    if (key === 'timestamp') return String(value).substring(0, 19).replace('T', ' ') || '-';
    if (key === 'latitude' || key === 'longitude') return typeof value === 'number' ? value.toFixed(6) : String(value);
    if (key === 'comment' || key === 'image') return String(value).substring(0, 20) || '-';
    return String(value) || '-';
  };

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
      {error && <Alert severity="error">{error}</Alert>}

      {importResult ? (
        // Success Result
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          <Alert severity="success">
            Import completed successfully!
          </Alert>
          <Box>
            <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1 }}>
              Summary:
            </Typography>
            <Typography variant="body2">
              Total Records: {importResult.summary.totalRecordsInFile}
            </Typography>
            <Typography variant="body2" sx={{ color: 'green' }}>
              Successfully Imported: {importResult.summary.successfullyImported}
            </Typography>
            {importResult.summary.skippedRecords > 0 && (
              <Typography variant="body2" sx={{ color: 'orange' }}>
                Skipped: {importResult.summary.skippedRecords}
              </Typography>
            )}
          </Box>
          {importResult.summary.errors.length > 0 && (
            <Box>
              <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1 }}>
                Errors (first 10):
              </Typography>
              {importResult.summary.errors.slice(0, 10).map((err: any, idx: number) => (
                <Typography key={idx} variant="body2" sx={{ color: '#d32f2f', fontSize: '0.875rem' }}>
                  Record {err.recordIndex}: {err.reason}
                </Typography>
              ))}
              {importResult.summary.errors.length > 10 && (
                <Typography variant="body2" sx={{ color: '#d32f2f' }}>
                  ... and {importResult.summary.errors.length - 10} more
                </Typography>
              )}
            </Box>
          )}
          <Box>
            <Typography variant="body2">
              <strong>Track ID:</strong> {importResult.trackId}
            </Typography>
            <Typography variant="body2">
              <strong>Track Name:</strong> {importResult.trackName}
            </Typography>
          </Box>
          <Button onClick={handleReset} variant="outlined" fullWidth>
            Import Another File
          </Button>
        </Box>
      ) : !parseData ? (
        // File Upload
        <Box
          onDragOver={handleDragOver}
          onDragLeave={handleDragLeave}
          onDrop={handleDrop}
          sx={{
            border: '2px dashed #ccc',
            borderRadius: 2,
            p: 4,
            textAlign: 'center',
            cursor: 'pointer',
            backgroundColor: dragOverRef.current ? '#f0f0f0' : 'transparent',
            transition: 'background-color 0.2s'
          }}
        >
          <Typography variant="h6" sx={{ mb: 1 }}>
            Drag and drop your GPX or GeoJSON file here
          </Typography>
          <Typography variant="body2" color="textSecondary" sx={{ mb: 2 }}>
            or click to select a file
          </Typography>
          <input
            ref={fileInputRef}
            type="file"
            accept=".gpx,.geojson,.json"
            onChange={(e) => {
              if (e.target.files?.[0]) {
                handleFileSelect(e.target.files[0]);
              }
            }}
            disabled={loading}
            style={{ display: 'none' }}
          />
          <Button
            variant="contained"
            onClick={() => fileInputRef.current?.click()}
            disabled={loading}
          >
            {loading ? 'Parsing...' : 'Select File'}
          </Button>
        </Box>
      ) : (
        // Mapping and Import
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
          {importing && (
            <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 1 }}>
              <Typography variant="body2">Importing... This may take a moment.</Typography>
              <LinearProgress sx={{ width: '100%' }} />
            </Box>
          )}

          <Box>
            <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 2 }}>
              Track Name
            </Typography>
            <TextField
              label="Track Name"
              value={trackName}
              onChange={(e) => setTrackName(e.target.value)}
              fullWidth
              variant="outlined"
              size="small"
              disabled={importing}
            />
          </Box>

          <Accordion defaultExpanded>
            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
              <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                Preview Data
              </Typography>
            </AccordionSummary>
            <AccordionDetails>
              <TableContainer component={Paper} elevation={0}>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      {COLUMNS.map(col => (
                        <TableCell key={col.key} sx={{ fontWeight: 600 }}>{col.label}</TableCell>
                      ))}
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {previewRecords.map((record: any) => (
                      <TableRow key={record.index}>
                        {COLUMNS.map(col => (
                          <TableCell key={col.key}>
                            {formatCellValue(record[col.key], col.key)}
                          </TableCell>
                        ))}
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
              <Typography variant="body2" color="textSecondary" sx={{ mt: 1 }}>
                Showing {previewRecords.length} of {parseData.totalRecords} records
              </Typography>
            </AccordionDetails>
          </Accordion>

          <Accordion defaultExpanded={false}>
            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
              <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                Mapping Configuration
              </Typography>
            </AccordionSummary>
            <AccordionDetails>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3, width: '100%' }}>
                {COLUMNS.map((column) => {
                  const mapping = columnMapping[column.key];

                  return (
                    <Box key={column.key} sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                      <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                        {column.label}
                      </Typography>
                      <ToggleButtonGroup
                        value={mapping?.type || 'null'}
                        exclusive
                        onChange={(_e, newType) => {
                          if (newType !== null) {
                            updateMapping(column.key, {
                              type: newType,
                              sourceField: newType === 'from_file' ? (mapping?.sourceField || column.key) : undefined,
                              staticValue: newType === 'static' ? (mapping?.staticValue || '') : undefined,
                            });
                          }
                        }}
                        disabled={importing}
                        size="small"
                        fullWidth
                      >
                        <ToggleButton value="from_file" aria-label="field">
                          Field
                        </ToggleButton>
                        <ToggleButton value="static" aria-label="static">
                          Static
                        </ToggleButton>
                        {column.nullable && (
                          <ToggleButton value="null" aria-label="null">
                            Null
                          </ToggleButton>
                        )}
                      </ToggleButtonGroup>

                      {mapping?.type === 'static' && (
                        <TextField
                          label="Static Value"
                          value={mapping.staticValue || ''}
                          onChange={(e) => {
                            updateMapping(column.key, {
                              type: 'static',
                              staticValue: e.target.value,
                            });
                          }}
                          size="small"
                          fullWidth
                          disabled={importing}
                        />
                      )}

                      {mapping?.type === 'from_file' && (
                        <FormControl size="small" fullWidth disabled={importing}>
                          <InputLabel>Select Field</InputLabel>
                          <Select
                            label="Select Field"
                            value={mapping.sourceField || ''}
                            onChange={(e) => {
                              updateMapping(column.key, {
                                type: 'from_file',
                                sourceField: e.target.value,
                              });
                            }}
                          >
                            {availableFields.map((field: string) => (
                              <MenuItem key={field} value={field}>
                                {fieldDisplayName[field] || field}
                              </MenuItem>
                            ))}
                          </Select>
                        </FormControl>
                      )}
                    </Box>
                  );
                })}

                <Box>
                  <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1 }}>
                    Additional Data (JSON)
                  </Typography>
                  <FormControl size="small" fullWidth disabled={importing}>
                    <InputLabel>Fields to store as JSON</InputLabel>
                    <Select
                      multiple
                      value={unmappedFieldsToStore}
                      onChange={(e) => {
                        const val = e.target.value;
                        setUnmappedFieldsToStore(typeof val === 'string' ? val.split(',') : val);
                      }}
                      input={<OutlinedInput label="Fields to store as JSON" />}
                      renderValue={(selected) => (
                        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                          {selected.map((field) => (
                            <Chip key={field} label={fieldDisplayName[field] || field} size="small" />
                          ))}
                        </Box>
                      )}
                    >
                      {availableFields.map((field: string) => (
                        <MenuItem key={field} value={field}>
                          {fieldDisplayName[field] || field}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Box>
              </Box>
            </AccordionDetails>
          </Accordion>

          <Alert severity="info">
            Ready to import {parseData.totalRecords} positions into a new track.
            {unmappedFieldsToStore.length > 0 && (
              <> {unmappedFieldsToStore.length} unmapped field(s) will be stored as JSON.</>
            )}
          </Alert>

          <Box sx={{ display: 'flex', gap: 1 }}>
            <Button
              onClick={handleReset}
              variant="outlined"
              fullWidth
              disabled={importing}
            >
              Cancel
            </Button>
            <Button
              onClick={handleImport}
              variant="contained"
              fullWidth
              disabled={importing || !trackName}
            >
              {importing ? 'Importing...' : 'Import'}
            </Button>
          </Box>
        </Box>
      )}
    </Box>
  );
};

export default ImportDrawer;
